import { useEffect, useRef, useState } from 'react';
import { transformPluginsApi, TransformPluginRow } from './api/transform-plugins-client';

// The 7 transform families supported by the DSL.
const FAMILIES = ['string', 'literal', 'cross-field', 'logic', 'lookup', 'number', 'date'] as const;

interface IdentityPanelProps {
  plugin: TransformPluginRow;
  onUpdate: (updated: TransformPluginRow) => void;
}

export function IdentityPanel({ plugin, onUpdate }: IdentityPanelProps) {
  const [label, setLabel] = useState(plugin.label);
  const [description, setDescription] = useState(plugin.description);
  const [family, setFamily] = useState(plugin.family);
  const [sampleSeeds, setSampleSeeds] = useState(plugin.sampleSeeds);

  // Skip the debounced save on initial render — only fire after the user
  // makes an actual edit.
  const isFirstRender = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      const updated = await transformPluginsApi.update(plugin.id, {
        label,
        description,
        family,
        sampleSeeds,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      onUpdate(updated);
    }, 800);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // onUpdate intentionally omitted — vi.fn() changes identity per render
    // and including it would re-trigger the debounce. Parent must wrap in
    // useCallback if it needs the dep to be tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, description, family, sampleSeeds, plugin.id]);

  function handleSeedsChange(raw: string) {
    const seeds = raw.split('\n').map((s) => s.trim()).filter(Boolean);
    setSampleSeeds(seeds);
  }

  return (
    <div className="rb-section">
      <h2 className="rb-section-title">Identity Panel</h2>

      <div className="rb-row">
        <label htmlFor="identity-pluginId" className="rb-label">
          Plugin ID
        </label>
        <input
          id="identity-pluginId"
          className="rb-input"
          value={plugin.pluginId}
          readOnly
          aria-readonly="true"
        />
      </div>

      <div className="rb-row">
        <label htmlFor="identity-label" className="rb-label">
          Label
        </label>
        <input
          id="identity-label"
          className="rb-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>

      <div className="rb-row">
        <label htmlFor="identity-description" className="rb-label">
          Description
        </label>
        <textarea
          id="identity-description"
          className="rb-input"
          value={description}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="rb-row">
        <label htmlFor="identity-family" className="rb-label">
          Family
        </label>
        <select
          id="identity-family"
          className="rb-select"
          value={family}
          onChange={(e) => setFamily(e.target.value)}
        >
          {FAMILIES.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>

      <div className="rb-row">
        <label htmlFor="identity-sampleSeeds" className="rb-label">
          Sample Seeds (one per line)
        </label>
        <textarea
          id="identity-sampleSeeds"
          className="rb-input"
          value={sampleSeeds.join('\n')}
          rows={4}
          onChange={(e) => handleSeedsChange(e.target.value)}
        />
      </div>
    </div>
  );
}
